/**
 * The scorecard surface of an interview — WP-W4.
 *
 * Two audiences, two operations, one panel:
 *
 *  - **The interviewer files their own scorecard** — `POST
 *    /recruiting/interviews/{id}/scorecard`, on `recruiting.submit_feedback`. The one
 *    invariant this whole task turns on: **the interviewer is the session employee, and
 *    is never a form field.** There is no "who" input here — the server stamps
 *    `interviewer_employee_id` from the session, so a caller cannot submit as someone
 *    else. The panel only shows *whose* scorecard this is, read-only. A resubmit
 *    updates the caller's own row (one per `(interview, interviewer)`).
 *  - **The not-on-panel refusal** — the permission gets a caller to the endpoint, and
 *    the service still refuses `403 WORKFORCE_SCOPE_DENIED` unless the session employee
 *    is on the panel. That is surfaced as a plain sentence — "you are not on this
 *    interview's panel" — rather than the generic permission-denied wording, because it
 *    is a fact about *this* interview, not about the caller's role.
 *  - **The hiring manager reads the whole panel's feedback** — `GET
 *    /recruiting/interviews/{id}/scorecards`, on `recruiting.view_candidates`, read
 *    only. Shown only when the caller holds that permission; an interviewer with just
 *    `submit_feedback` does not see the others' cards (the server would refuse the
 *    read), so the section is not offered to them.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type {
  RecruitingScorecard,
  RecruitingScorecardSubmitRequest,
  ScorecardRecommendation,
} from '../../api/recruiting';
import { SCORECARD_RECOMMENDATIONS } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import type { QueryStatus } from '../../api/useApiQuery';
import { RECOMMENDATION_BADGE, RECOMMENDATION_LABEL } from '../../recruiting/interview';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { SEVERITY_ICON } from '../../states/icons';
import { ErrorCodeLine } from '../../states/ErrorCodeLine';

/** 1..5, the contract's `overall` range. */
const OVERALL_SCORES = [1, 2, 3, 4, 5] as const;

interface CompetencyRow {
  readonly name: string;
  readonly score: '' | number;
}

export interface ScorecardPanelProps {
  /** The session employee — shown read-only as the interviewer, never an input. */
  readonly meLabel: string;
  /** The caller's own scorecard, from the submit response or the readable list; `null` if none yet. */
  readonly ownScorecard: RecruitingScorecard | null;
  readonly submitPending: boolean;
  readonly submitError: WorkforceApiError | null;
  readonly onSubmit: (body: RecruitingScorecardSubmitRequest) => void;
  /** Whether the caller holds `recruiting.view_candidates` — gates the all-scorecards read. */
  readonly canViewAll: boolean;
  readonly allStatus: QueryStatus;
  readonly allScorecards: readonly RecruitingScorecard[] | null;
  readonly allError: WorkforceApiError | null;
  readonly onReloadAll: () => void;
  readonly locale: string;
  readonly timezone: string;
}

export function ScorecardPanel({
  meLabel,
  ownScorecard,
  submitPending,
  submitError,
  onSubmit,
  canViewAll,
  allStatus,
  allScorecards,
  allError,
  onReloadAll,
  locale,
  timezone,
}: ScorecardPanelProps): ReactNode {
  const { t } = useI18n();
  const [recommendation, setRecommendation] = useState<'' | ScorecardRecommendation>('');
  const [overall, setOverall] = useState<'' | number>('');
  const [comments, setComments] = useState('');
  const [competencies, setCompetencies] = useState<readonly CompetencyRow[]>([]);

  const ready = recommendation !== '' && overall !== '';

  // The not-on-panel refusal is a fact about this interview, not the caller's role, so
  // it reads as a plain sentence rather than the generic permission-denied wording.
  const notOnPanel = submitError !== null && submitError.code === 'WORKFORCE_SCOPE_DENIED';

  const build = (): RecruitingScorecardSubmitRequest => {
    if (recommendation === '' || overall === '') {
      throw new Error('unreachable: submit guarded by `ready`');
    }
    const map: Record<string, number> = {};
    for (const row of competencies) {
      const name = row.name.trim();
      if (name !== '' && row.score !== '') map[name] = row.score;
    }
    return {
      recommendation,
      overall,
      ...(comments.trim() === '' ? {} : { comments: comments.trim() }),
      ...(Object.keys(map).length === 0 ? {} : { competencies_json: map }),
    };
  };

  return (
    <section
      className="wp-section__subsection"
      aria-labelledby="w4-scorecard-heading"
      data-testid="w4-scorecard"
    >
      <h3 className="wp-section__title" id="w4-scorecard-heading">
        {t('recruiting.interviews.scorecard.title')}
      </h3>

      {/* The interviewer is the session employee — stated, never chosen. */}
      <p className="wp-small wp-muted" data-testid="w4-scorecard-me">
        {t('recruiting.interviews.scorecard.interviewer', { name: meLabel })}
      </p>

      {ownScorecard === null ? null : (
        <StatusNote
          severity="low"
          title={t('recruiting.interviews.scorecard.ownState')}
          testId="w4-scorecard-own"
        >
          <p className="wp-small">
            {ownScorecard.recommendation === null
              ? t('recruiting.interviews.scorecard.noRecommendation')
              : t(RECOMMENDATION_LABEL[ownScorecard.recommendation])}
            {ownScorecard.overall === null
              ? null
              : ` · ${t('recruiting.interviews.scorecard.overallShort', { score: ownScorecard.overall })}`}
          </p>
          {ownScorecard.submitted_at === null ? null : (
            <p className="wp-small wp-muted">
              {t('recruiting.interviews.scorecard.submittedAt', {
                when: formatInstant(ownScorecard.submitted_at, locale, timezone),
              })}
            </p>
          )}
        </StatusNote>
      )}

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || submitPending) return;
          onSubmit(build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-scorecard-recommendation">
            {t('recruiting.interviews.scorecard.recommendation')}
          </label>
          <select
            className="wp-field__input"
            id="w4-scorecard-recommendation"
            data-testid="w4-scorecard-recommendation"
            value={recommendation}
            onChange={(event) => {
              const next = event.target.value;
              setRecommendation(next === '' ? '' : (next as ScorecardRecommendation));
            }}
          >
            <option value="">
              {t('recruiting.interviews.scorecard.recommendationPlaceholder')}
            </option>
            {SCORECARD_RECOMMENDATIONS.map((value) => (
              <option key={value} value={value}>
                {t(RECOMMENDATION_LABEL[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-scorecard-overall">
            {t('recruiting.interviews.scorecard.overall')}
          </label>
          <select
            className="wp-field__input"
            id="w4-scorecard-overall"
            data-testid="w4-scorecard-overall"
            value={overall === '' ? '' : String(overall)}
            onChange={(event) => {
              const next = event.target.value;
              setOverall(next === '' ? '' : Number(next));
            }}
          >
            <option value="">{t('recruiting.interviews.scorecard.overallPlaceholder')}</option>
            {OVERALL_SCORES.map((value) => (
              <option key={value} value={String(value)}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="wp-filters__group" data-testid="w4-scorecard-competencies">
          <legend className="wp-filters__legend">
            {t('recruiting.interviews.scorecard.competencies')}
          </legend>
          <p className="wp-small wp-muted">
            {t('recruiting.interviews.scorecard.competenciesHint')}
          </p>
          {competencies.map((row, index) => (
            <div
              className="wp-filters__group"
              key={index}
              data-testid={`w4-scorecard-competency-${index}`}
            >
              <input
                className="wp-field__input"
                aria-label={t('recruiting.interviews.scorecard.competencyName')}
                data-testid={`w4-scorecard-competency-name-${index}`}
                type="text"
                value={row.name}
                autoComplete="off"
                placeholder={t('recruiting.interviews.scorecard.competencyName')}
                onChange={(event) => {
                  const value = event.target.value;
                  setCompetencies((rows) =>
                    rows.map((existing, i) =>
                      i === index ? { ...existing, name: value } : existing,
                    ),
                  );
                }}
              />
              <select
                className="wp-field__input"
                aria-label={t('recruiting.interviews.scorecard.competencyScore')}
                data-testid={`w4-scorecard-competency-score-${index}`}
                value={row.score === '' ? '' : String(row.score)}
                onChange={(event) => {
                  const next = event.target.value;
                  setCompetencies((rows) =>
                    rows.map((existing, i) =>
                      i === index
                        ? { ...existing, score: next === '' ? '' : Number(next) }
                        : existing,
                    ),
                  );
                }}
              >
                <option value="">{t('recruiting.interviews.scorecard.competencyScore')}</option>
                {OVERALL_SCORES.map((value) => (
                  <option key={value} value={String(value)}>
                    {value}
                  </option>
                ))}
              </select>
              <button
                className="wp-button"
                type="button"
                data-testid={`w4-scorecard-competency-remove-${index}`}
                onClick={() => {
                  setCompetencies((rows) => rows.filter((_, i) => i !== index));
                }}
              >
                {t('recruiting.interviews.scorecard.competencyRemove')}
              </button>
            </div>
          ))}
          <button
            className="wp-button"
            type="button"
            data-testid="w4-scorecard-competency-add"
            onClick={() => {
              setCompetencies((rows) => [...rows, { name: '', score: '' }]);
            }}
          >
            {t('recruiting.interviews.scorecard.competencyAdd')}
          </button>
        </fieldset>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-scorecard-comments">
            {t('recruiting.interviews.scorecard.comments')}
          </label>
          <textarea
            className="wp-field__input"
            id="w4-scorecard-comments"
            data-testid="w4-scorecard-comments"
            value={comments}
            rows={3}
            onChange={(event) => {
              setComments(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w4-scorecard-submit"
            disabled={!ready || submitPending}
          >
            {submitPending
              ? t('recruiting.interviews.scorecard.submitting')
              : ownScorecard === null
                ? t('recruiting.interviews.scorecard.submit')
                : t('recruiting.interviews.scorecard.resubmit')}
          </button>
        </div>
      </form>

      {notOnPanel ? (
        <StatusNote
          severity="medium"
          role="alert"
          title={t('recruiting.interviews.scorecard.notOnPanel.title')}
          testId="w4-scorecard-not-on-panel"
        >
          <p>{t('recruiting.interviews.scorecard.notOnPanel.body')}</p>
          <ErrorCodeLine code={submitError.code} correlationId={submitError.correlationId} />
        </StatusNote>
      ) : submitError === null ? null : (
        <ApiErrorNote
          error={submitError}
          subject={t('recruiting.interviews.scorecard.subject')}
          testId="w4-scorecard-error"
        />
      )}

      {canViewAll ? (
        <div className="wp-section__subsection" data-testid="w4-scorecards-all">
          <h4 className="wp-section__title">{t('recruiting.interviews.scorecard.allTitle')}</h4>
          {allStatus === 'loading' ? (
            <Skeleton
              rows={2}
              variant="block"
              label={t('recruiting.interviews.scorecard.subject')}
              testId="w4-scorecards-skeleton"
            />
          ) : null}
          {allStatus === 'error' && allError !== null ? (
            <ApiErrorNote
              error={allError}
              onRetry={onReloadAll}
              subject={t('recruiting.interviews.scorecard.subject')}
              testId="w4-scorecards-error"
            />
          ) : null}
          {allScorecards === null ? null : allScorecards.length === 0 ? (
            <EmptyState
              title={t('recruiting.interviews.scorecard.allEmpty.title')}
              description={t('recruiting.interviews.scorecard.allEmpty.body')}
              testId="w4-scorecards-empty"
            />
          ) : (
            <ul className="wp-rows" data-testid="w4-scorecards-list">
              {allScorecards.map((card) => (
                <li className="wp-row" key={card.id} data-testid={`w4-scorecard-row-${card.id}`}>
                  <div className="wp-row__main">
                    <p>
                      {card.recommendation === null ? null : (
                        <Badge
                          severity={RECOMMENDATION_BADGE[card.recommendation]}
                          icon={SEVERITY_ICON[RECOMMENDATION_BADGE[card.recommendation]]}
                          label={t(RECOMMENDATION_LABEL[card.recommendation])}
                          testId={`w4-scorecard-rec-${card.id}`}
                        />
                      )}{' '}
                      <span>
                        {t('recruiting.interviews.scorecard.rowInterviewer', {
                          who: card.interviewer_employee_id,
                        })}
                      </span>
                    </p>
                    <p className="wp-small wp-muted">
                      {card.overall === null
                        ? t('recruiting.interviews.scorecard.noOverall')
                        : t('recruiting.interviews.scorecard.overallShort', {
                            score: card.overall,
                          })}
                      {card.comments === null ? '' : ` · ${card.comments}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <p className="wp-small wp-muted" data-testid="w4-scorecards-hidden">
          {t('recruiting.interviews.scorecard.allHidden')}
        </p>
      )}
    </section>
  );
}
